import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react'
import { authConfigured, socialProviders } from '../amplify'
import {
  listSessions,
  uploadSession,
  downloadSession,
  deleteSession,
  loadMeta,
  mergeMeta,
  describeInfo,
  loadCloudState,
  saveCloudState,
  type SessionInfo,
  type SessionInfos,
  type StoredSession,
} from '../lib/storage'
import { bestLap } from '../analysis/laps'
import { haversine } from '../analysis/lapFilter'
import type { Session } from '../xrk/types'
import {
  EMPTY_VIEW,
  deleteFile,
  getFile,
  loadLocal,
  pruneFiles,
  putFile,
  saveLocal,
  type SessionRef,
  type ViewState,
} from '../lib/persist'
import Analysis, { type LoadedSession } from '../components/Analysis'
import Solo2Panel from '../components/Solo2Panel'
import TabBar, { type Tab } from '../components/TabBar'
import { solo2Available } from '../solo2/bridge'
import { deviceLabel, findDevice, useDevices } from '../lib/devices'
import {
  cachedAsStored,
  deleteDocument,
  deleteEverywhere,
  exportToFiles,
  listDocuments,
  readDocument,
  type DocumentFile,
  isOnline,
  offlineAvailable,
  queueForUpload,
  readCachedFile,
  readIndex,
  rememberInfo,
  saveCachedFile,
  statusOf,
  syncRepo,
  clearOffline,
  type SyncStatus,
} from '../lib/offline'
import { Network } from '@capacitor/network'
import { nativePickerAvailable, pickSessionFiles } from '../lib/filePicker'

/**
 * Race Studio is open to everyone: local files can be analysed without an account.
 * Signing in is only needed for the per-user cloud repo, and is offered in a modal.
 */
export default function RaceStudio() {
  if (!authConfigured) {
    return <Workspace auth={null} />
  }
  return <AuthAwareWorkspace />
}

type AuthReason = 'signin' | 'upload'

export interface AuthState {
  signedIn: boolean
  userLabel: string
  signOut: () => void
  requestSignIn: (reason: AuthReason) => void
}

function AuthAwareWorkspace() {
  const { authStatus, user, signOut } = useAuthenticator((ctx) => [ctx.authStatus, ctx.user])
  const [reason, setReason] = useState<AuthReason | null>(null)
  const signedIn = authStatus === 'authenticated'

  useEffect(() => {
    if (signedIn) setReason(null)
  }, [signedIn])

  // The home page links here with ?signin=1 to open the sign-in dialog.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('signin')) {
      setReason('signin')
      params.delete('signin')
      const q = params.toString()
      window.history.replaceState(null, '', window.location.pathname + (q ? `?${q}` : ''))
    }
  }, [])

  const auth: AuthState = {
    signedIn,
    userLabel: user?.signInDetails?.loginId ?? user?.username ?? '',
    signOut,
    requestSignIn: setReason,
  }

  return (
    <>
      <Workspace auth={auth} />
      {reason && !signedIn && (
        <div className="modal-backdrop" onClick={() => setReason(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="link modal-close" onClick={() => setReason(null)} aria-label="Close">
              ×
            </button>
            {reason === 'upload' && (
              <p className="modal-note">
                Sign in or create a free account to upload sessions to your repo and open them from any device.
              </p>
            )}
            <Authenticator loginMechanisms={['email']} signUpAttributes={['email']} socialProviders={socialProviders} />
          </div>
        </div>
      )}
    </>
  )
}

/** Natural compare: a_0392 sorts after a_0391, and after a_392. */
export function compareNames(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
}

function newId(): string {
  return `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`
}

function Workspace({ auth }: { auth: AuthState | null }) {
  const [sessions, setSessions] = useState<LoadedSession[]>([])
  const sessionsRef = useRef<LoadedSession[]>([])
  sessionsRef.current = sessions
  const [view, setView] = useState<ViewState>(EMPTY_VIEW)
  const [errors, setErrors] = useState<string[]>([])
  const [busy, setBusy] = useState(0)
  const [cloud, setCloud] = useState<StoredSession[]>([])
  /** Date / laps / best / track per repo file, from the logger's catalog or the parsed file */
  const [meta, setMeta] = useState<SessionInfos>({})
  const metaRef = useRef<SessionInfos>({})
  metaRef.current = meta
  /** Sessions in the Files-app folder of the app (iOS only) */
  const [docs, setDocs] = useState<DocumentFile[]>([])
  const refreshDocs = useCallback(async () => {
    if (!offlineAvailable) return
    setDocs(await listDocuments())
  }, [])
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [exportMode, setExportMode] = useState<'png' | 'ai' | null>(null)
  const knownDevices = useDevices()
  // Diagrams call a session by its logger's nickname; two sessions from one logger keep the file name too.
  const aliases = useMemo(() => {
    const out: Record<string, string> = {}
    const names = sessions.map((s) => s.name)
    const nickOf = (name: string) => deviceLabel(findDevice(meta[name]?.device)) || ''
    for (const name of names) {
      const nick = nickOf(name)
      if (!nick) continue
      const shared = names.filter((n) => n !== name && nickOf(n) === nick).length > 0
      out[name] = shared ? `${nick} ${name.replace(/\.(xrk|xrz|hrz)$/i, '')}` : nick
    }
    return out
  }, [sessions, meta, knownDevices])
  const [tab, setTab] = useState<Tab>(() => {
    try {
      const t = localStorage.getItem('mala.tab') as Tab | null
      return t === 'repo' || (t === 'device' && solo2Available) ? t : 'analysis'
    } catch {
      return 'analysis'
    }
  })
  const switchTab = useCallback((t: Tab) => {
    setTab(t)
    try {
      localStorage.setItem('mala.tab', t)
    } catch {
      /* storage may be unavailable */
    }
  }, [])
  const [notice, setNotice] = useState('')
  useEffect(() => {
    if (!notice) return
    const t = setTimeout(() => setNotice(''), 4000)
    return () => clearTimeout(t)
  }, [notice])
  const [loadingKeys, setLoadingKeys] = useState<Set<string>>(new Set())
  const [restored, setRestored] = useState(false)
  /** Session ids whose selection came from saved state; Analysis must not auto-pick laps for them. */
  const restoredIds = useRef<Set<string>>(new Set())
  const signedIn = auth?.signedIn ?? false

  const [refreshing, setRefreshing] = useState(false)
  async function onRefreshCloud() {
    setRefreshing(true)
    try {
      await refreshCloud()
    } finally {
      setRefreshing(false)
    }
  }

  // ---- Offline copy of the repo (iOS app only, always on) ----
  const offlineOn = offlineAvailable
  const [sync, setSync] = useState<SyncStatus | null>(null)
  const [cachedNames, setCachedNames] = useState<Set<string>>(new Set())
  const [pendingNames, setPendingNames] = useState<Set<string>>(new Set())
  const [servedFromPhone, setServedFromPhone] = useState(false)

  const refreshCached = useCallback(async () => {
    if (!offlineAvailable) return
    const index = await readIndex()
    setCachedNames(new Set(Object.keys(index.files)))
    // Info for sessions only on this phone lives in the index; what is already known wins.
    if (index.meta && Object.keys(index.meta).length) setMeta((m) => ({ ...index.meta, ...m }))
    setPendingNames(
      new Set(
        Object.values(index.files)
          .filter((f) => f.pending)
          .map((f) => f.name),
      ),
    )
    setSync((s) =>
      statusOf(index, s?.state === 'syncing' ? 'syncing' : s?.state === 'offline' ? 'offline' : 'idle', s?.error),
    )
  }, [])

  const runSync = useCallback(async () => {
    if (!offlineAvailable || !offlineOn) return
    const status = await syncRepo((s) => setSync(s))
    setSync(status)
    await refreshCached()
    if (status.state === 'idle') {
      try {
        const list = await listSessions()
        setCloud(list)
      } catch {
        /* keep what we have */
      }
    }
  }, [offlineOn, refreshCached])

  const refreshCloud = useCallback(async () => {
    if (!signedIn) {
      setCloud([])
      return
    }
    // In the app, push the upload queue and pull changes first, so the list that follows is current.
    if (offlineAvailable && offlineOn) {
      const status = await syncRepo((st) => setSync(st))
      setSync(status)
      await refreshCached()
      if (status.state === 'offline') {
        // No connection: the copy on this phone is the repo for now.
        const index = await readIndex()
        setCloud(cachedAsStored(index))
        setMeta({ ...(index.meta ?? {}) })
        setServedFromPhone(true)
        return
      }
    }
    try {
      const [list, m] = await Promise.all([listSessions(), loadMeta()])
      setCloud(list)
      // The repo's info, over whatever this phone remembers for its own files.
      const local = offlineAvailable ? ((await readIndex()).meta ?? {}) : {}
      setMeta({ ...local, ...m })
      setServedFromPhone(false)
    } catch (e) {
      setErrors((errs) => [...errs, String(e)])
    }
  }, [signedIn, offlineOn, refreshCached])

  useEffect(() => {
    void refreshCloud()
  }, [refreshCloud])

  useEffect(() => {
    void refreshCached()
  }, [refreshCached])

  // Keep the queue moving: on any network change, whenever the app comes to the foreground, and
  // once a minute while something is waiting to upload (leaving the logger's hotspot for home
  // Wi-Fi is "wifi" before and after, so it does not always count as a change).
  useEffect(() => {
    if (!offlineAvailable || !offlineOn || !signedIn) return
    const sub = Network.addListener('networkStatusChange', (st) => {
      if (st.connected) void refreshCloud()
    })
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshCloud()
    }
    document.addEventListener('visibilitychange', onVisible)
    const timer = setInterval(() => {
      if ((sync?.pending ?? 0) > 0 && sync?.state !== 'syncing') void refreshCloud()
    }, 60_000)
    return () => {
      void sub.then((h) => h.remove())
      document.removeEventListener('visibilitychange', onVisible)
      clearInterval(timer)
    }
  }, [offlineOn, signedIn, refreshCloud, sync?.pending, sync?.state])

  // Signed out: the mirror belongs to that account, so drop it.
  useEffect(() => {
    if (!offlineAvailable || signedIn) return
    void clearOffline().then(() => {
      setCachedNames(new Set())
      setSync(null)
      setServedFromPhone(false)
    })
  }, [signedIn])

  /** Parse a file and add it as a session. The bytes are cached so a refresh can reopen it. */
  const loadBuffer = useCallback(
    async (name: string, buf: ArrayBuffer, cloudKey?: string, id = newId(), cache = true) => {
      setBusy((b) => b + 1)
      try {
        if (cache) await putFile({ id, name, cloudKey, bytes: buf.slice(0) })
        const { parseXrk } = await import('../xrk/parse')
        const session = await parseXrk(buf)
        // One track at a time: laps from different circuits cannot share sections or a distance axis.
        const clash = sessionsRef.current.find((s) => s.id !== id && differentTrack(s.session, session))
        if (clash) {
          setErrors((errs) => [
            ...errs,
            `${name} is from ${session.meta.track ?? 'another track'}; the open sessions are from ${clash.session.meta.track ?? 'a different track'}. Close them first.`,
          ])
          if (cache) void deleteFile(id)
          return
        }
        setSessions((cur) => (cur.some((s) => s.id === id) ? cur : [...cur, { id, name, session, cloudKey }]))
        // The file itself is the truth about a session: files without info yet get it from the
        // parse, and a date the logger's catalog got wrong (day/month order) is corrected.
        const existing = metaRef.current[name]
        const parsedDate = session.meta.date?.toISOString()
        const dateOff =
          !!parsedDate &&
          (!existing?.date || Math.abs(Date.parse(existing.date) - Date.parse(parsedDate)) > 24 * 3600 * 1000)
        if ((!existing || dateOff) && (cloudKey || offlineAvailable)) {
          const best = bestLap(session.laps)
          const info: SessionInfo = {
            ...existing,
            date: parsedDate,
            laps: session.laps.length,
            bestMs: best ? Math.round(best.duration * 1000) : undefined,
            track: session.meta.track,
          }
          setMeta((m) => ({ ...m, [name]: info }))
          if (cloudKey) mergeMeta({ [name]: info }).catch(() => undefined)
          else rememberInfo(name, info).catch(() => undefined)
        }
      } catch (e) {
        setErrors((errs) => [...errs, `Could not read ${name}: ${e instanceof Error ? e.message : String(e)}`])
        if (cache) void deleteFile(id)
      } finally {
        setBusy((b) => b - 1)
      }
    },
    [],
  )

  // ---- Restore the last workspace from this browser ----
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const local = loadLocal()
      if (local) {
        if (local.sidebarOpen != null) setSidebarOpen(local.sidebarOpen)
        for (const ref of local.sessions) restoredIds.current.add(ref.id)
        // Open sessions in their saved order, then apply the saved selection once they all exist.
        for (const ref of local.sessions) {
          if (cancelled) return
          const f = await getFile(ref.id)
          if (f) await loadBuffer(f.name, f.bytes, f.cloudKey, ref.id, false)
        }
        if (!cancelled) setView(local.view)
      }
      if (!cancelled) {
        // With nothing open, the sidebar is the only way forward: show it.
        if (!local || local.sessions.length === 0) setSidebarOpen(true)
        setRestored(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [loadBuffer])

  // ---- Persist to this browser whenever something changes ----
  useEffect(() => {
    if (!restored) return
    const refs: SessionRef[] = sessions.map((s) => ({ id: s.id, name: s.name, cloudKey: s.cloudKey }))
    saveLocal({ sessions: refs, view, sidebarOpen })
    void pruneFiles(refs.map((r) => r.id))
  }, [sessions, view, sidebarOpen, restored])

  // ---- Signed in with nothing open: pick up the workspace saved in the repo ----
  useEffect(() => {
    if (!signedIn || !restored || sessions.length > 0) return
    let cancelled = false
    ;(async () => {
      try {
        const state = await loadCloudState()
        if (!state || cancelled || state.sessions.length === 0) return
        for (const ref of state.sessions) restoredIds.current.add(ref.id)
        for (const ref of state.sessions) {
          if (cancelled) return
          try {
            await loadBuffer(ref.name, await downloadSession(ref.cloudKey), ref.cloudKey, ref.id)
          } catch {
            /* file may have been deleted since; skip it */
          }
        }
        if (!cancelled) setView(state.view)
      } catch (e) {
        setErrors((errs) => [...errs, String(e)])
      }
    })()
    return () => {
      cancelled = true
    }
    // Only when sign-in state flips or the restore completes; not on every session change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn, restored])

  // ---- Mirror repo sessions and selections to the repo (debounced) ----
  useEffect(() => {
    if (!signedIn || !restored) return
    const cloudSessions = sessions.filter((s): s is LoadedSession & { cloudKey: string } => !!s.cloudKey)
    const ids = new Set(cloudSessions.map((s) => s.id))
    const selected = view.selected.filter((k) => ids.has(k.split(':')[0]))
    const timer = setTimeout(() => {
      void saveCloudState({
        sessions: cloudSessions.map((s) => ({ id: s.id, name: s.name, cloudKey: s.cloudKey })),
        view: {
          selected,
          reference: view.reference && selected.includes(view.reference) ? view.reference : selected[0],
          channelNames: view.channelNames,
          showDelta: view.showDelta,
          showSummary: view.showSummary,
          showAccel: view.showAccel,
        },
      }).catch(() => {
        /* best effort */
      })
    }, 1000)
    return () => clearTimeout(timer)
  }, [sessions, view, signedIn, restored])

  /** Files picked but not yet opened: the user chooses "open" or "open and upload". */
  const [pendingFiles, setPendingFiles] = useState<File[]>([])

  async function onPickFiles(ev: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(ev.target.files ?? [])
    ev.target.value = ''
    await filesPicked(files)
  }

  /** iOS app: the native picker starts in On My iPhone › Mala Racing. */
  async function onPickNative() {
    try {
      const { files, skipped } = await pickSessionFiles()
      if (skipped.length) setErrors((errs) => [...errs, `Not session files: ${skipped.join(', ')}`])
      await filesPicked(files)
    } catch (e) {
      setErrors((errs) => [...errs, String(e)])
    }
  }

  async function filesPicked(files: File[]) {
    if (files.length === 0) return
    // Without a backend there is nothing to upload to: just open.
    if (!auth) await openFiles(files)
    else setPendingFiles(files)
  }

  /** Bundled sessions (web/public/samples) so the app can be tried without a logger. */
  const SAMPLES = ['a_0390.xrk', 'a_0392.xrk']
  const sampleOpen = SAMPLES.every((f) => sessions.some((s) => s.name === `sample ${f}`))
  async function openSample() {
    switchTab('analysis')
    setBusy((b) => b + 1)
    try {
      for (const f of SAMPLES) {
        if (sessions.some((s) => s.name === `sample ${f}`)) continue
        const res = await fetch(`${import.meta.env.BASE_URL}samples/${f}`)
        if (!res.ok) throw new Error(`Could not load the sample session ${f} (${res.status})`)
        await loadBuffer(`sample ${f}`, await res.arrayBuffer())
      }
    } catch (e) {
      setErrors((errs) => [...errs, String(e)])
    } finally {
      setBusy((b) => b - 1)
    }
  }

  async function openFiles(files: File[]) {
    setPendingFiles([])
    switchTab('analysis')
    await Promise.all(files.map(async (f) => loadBuffer(f.name, await f.arrayBuffer())))
  }

  /** Upload bytes to the repo and open them as a session bound to that repo file. */
  const uploadAndOpen = useCallback(
    async (name: string, bytes: ArrayBuffer) => {
      const key = await uploadSession(new File([bytes], name))
      await loadBuffer(name, bytes.slice(0), key)
      if (offlineAvailable && offlineOn) {
        await saveCachedFile(name, bytes, { key, lastModified: new Date().toISOString() })
        await refreshCached()
      }
    },
    [loadBuffer, offlineOn, refreshCached],
  )

  async function openAndUploadFiles(files: File[]) {
    if (!auth) return
    if (!signedIn) {
      auth.requestSignIn('upload')
      return
    }
    switchTab('analysis')
    setPendingFiles([])
    setBusy((b) => b + 1)
    try {
      for (const f of files) {
        const bytes = await f.arrayBuffer()
        try {
          await uploadAndOpen(f.name, bytes)
        } catch (e) {
          if (!(offlineAvailable && offlineOn)) throw e
          // No connection: open it now and let the sync upload it later.
          await loadBuffer(f.name, bytes.slice(0))
          await queueForUpload(f.name, bytes)
          await refreshCached()
        }
      }
      await refreshCloud()
    } catch (e) {
      setErrors((errs) => [...errs, String(e)])
    } finally {
      setBusy((b) => b - 1)
    }
  }

  const removeSession = useCallback((id: string) => {
    setSessions((cur) => cur.filter((s) => s.id !== id))
    void deleteFile(id)
  }, [])

  /** Repo checkbox: tick opens the file as a session, untick closes that session. */
  async function toggleCloud(item: StoredSession) {
    const open = sessions.find((s) => s.cloudKey === item.key)
    if (open) {
      removeSession(open.id)
      return
    }
    if (loadingKeys.has(item.key)) return
    setLoadingKeys((k) => new Set(k).add(item.key))
    try {
      const cached = offlineAvailable && cachedNames.has(item.name) ? await readCachedFile(item.name) : null
      if (cached) {
        await loadBuffer(item.name, cached, item.key)
      } else {
        const bytes = await downloadSession(item.key)
        // Parsing hands its buffer to a worker (detaching it), so the copy kept must be separate.
        await loadBuffer(item.name, bytes.slice(0), item.key)
        if (offlineAvailable && offlineOn) {
          await saveCachedFile(item.name, bytes, { key: item.key, lastModified: item.lastModified.toISOString() })
          await refreshCached()
        }
      }
    } catch (e) {
      setErrors((errs) => [...errs, String(e)])
    } finally {
      setLoadingKeys((k) => {
        const n = new Set(k)
        n.delete(item.key)
        return n
      })
    }
  }

  /** Keep a logger download on the phone (Files-app copy) and, if asked, queue it for the repo. */
  async function keepLoggerFile(name: string, bytes: ArrayBuffer, info: SessionInfo, upload: boolean) {
    if (!offlineAvailable) return
    setMeta((m) => ({ ...m, [name]: info }))
    await rememberInfo(name, info).catch(() => undefined)
    await exportToFiles(name, bytes).catch((e) =>
      setErrors((errs) => [...errs, `Could not save ${name} to Files: ${String(e)}`]),
    )
    await refreshDocs()
    if (upload && offlineOn && signedIn) {
      await queueForUpload(name, bytes, info)
      await refreshCached()
      if (await isOnline()) void runSync()
    }
  }

  /** "Download": keep it on the phone; it shows under My repo with an Upload button. */
  async function onLoggerFile(name: string, bytes: ArrayBuffer, info: SessionInfo) {
    await keepLoggerFile(name, bytes, info, false)
  }

  /** "Download and Upload": keep it and queue it for the repo. */
  async function onLoggerUpload(name: string, bytes: ArrayBuffer, info: SessionInfo) {
    await keepLoggerFile(name, bytes, info, true)
  }

  /** Upload a session that is on the phone: queued, then pushed right away when online. */
  async function uploadDoc(name: string) {
    if (!auth) return
    if (!signedIn) {
      auth.requestSignIn('upload')
      return
    }
    const bytes = await readDocument(name)
    if (!bytes) {
      setErrors((errs) => [...errs, `Could not read ${name} from this phone`])
      return
    }
    await queueForUpload(name, bytes, meta[name])
    await refreshCached()
    void refreshCloud()
  }
  async function uploadAllDocs() {
    if (!auth) return
    if (!signedIn) {
      auth.requestSignIn('upload')
      return
    }
    for (const d of docs) {
      if (inRepo.has(d.name) || pendingNames.has(d.name)) continue
      const bytes = await readDocument(d.name)
      if (bytes) await queueForUpload(d.name, bytes, meta[d.name])
    }
    await refreshCached()
    await refreshCloud()
  }

  /** One row per file name: in the repo, on the phone, or both. */
  const inRepo = new Set(cloud.map((c) => c.name))
  const merged = (() => {
    const byName = new Map<string, { name: string; cloud?: StoredSession; doc?: DocumentFile }>()
    for (const c of cloud) byName.set(c.name, { name: c.name, cloud: c })
    for (const d of docs) byName.set(d.name, { ...(byName.get(d.name) ?? { name: d.name }), doc: d })
    // Newest first by session date (kept per file on the phone and in the repo); names, which
    // the logger numbers sequentially, break ties and order files whose date is not known yet.
    const when = (name: string) => Date.parse(meta[name]?.date ?? '') || 0
    return [...byName.values()].sort((a, b) => when(b.name) - when(a.name) || compareNames(b.name, a.name))
  })()
  const uploadable = docs.filter((d) => !inRepo.has(d.name) && !pendingNames.has(d.name)).length

  async function toggleRow(row: { name: string; cloud?: StoredSession; doc?: DocumentFile }) {
    const open = sessions.find((s) => s.name === row.name)
    if (open) {
      removeSession(open.id)
      return
    }
    switchTab('analysis')
    // Prefer the copy on the phone: no network needed.
    if (row.doc) {
      const bytes = await readDocument(row.name)
      if (bytes) {
        await loadBuffer(row.name, bytes, row.cloud?.key)
        return
      }
    }
    if (row.cloud) await toggleCloud(row.cloud)
  }
  async function deleteRow(row: { name: string; cloud?: StoredSession; doc?: DocumentFile }) {
    const where = row.cloud && row.doc ? 'your repo and this phone' : row.cloud ? 'your repo' : 'this phone'
    if (!confirm(`Delete ${row.name} from ${where}?`)) return
    const open = sessions.find((s) => s.name === row.name)
    if (open) removeSession(open.id)
    if (row.cloud) {
      if (offlineAvailable) await deleteEverywhere(row.cloud)
      else await deleteSession(row.cloud.key)
    }
    if (row.doc) await deleteDocument(row.name)
    await refreshDocs()
    await refreshCached()
    await refreshCloud()
  }

  useEffect(() => {
    void refreshDocs()
  }, [refreshDocs])

  function closeAll() {
    for (const s of sessions) removeSession(s.id)
  }
  // One repo row of the My repo tab.
  function renderRow(row: { name: string; cloud?: StoredSession; doc?: DocumentFile }) {
    const isOpen = sessions.some((s) => s.name === row.name)
    const uploaded = !!row.cloud
    const from = deviceLabel(findDevice(meta[row.name]?.device))
    const queued = pendingNames.has(row.name)
    return (
      <li key={row.name}>
        <div className="cloud-row">
          <span className="cloud-name" title={row.name}>
            {from && <span className="device-tag">{from}</span>}
            <span>{row.name}</span>
          </span>
          <button
            className={`button row-button open-button${isOpen ? ' secondary' : ''}`}
            disabled={row.cloud ? loadingKeys.has(row.cloud.key) : false}
            onClick={() => void toggleRow(row)}
          >
            {isOpen ? 'Close' : 'Open'}
          </button>
          {offlineAvailable &&
            (uploaded ? (
              <button className="button row-button upload-button secondary" disabled>
                Uploaded
              </button>
            ) : queued ? (
              <button className="button row-button upload-button secondary" disabled>
                {sync?.state === 'syncing' ? 'Uploading…' : 'Queued'}
              </button>
            ) : (
              <button className="button row-button upload-button" onClick={() => void uploadDoc(row.name)}>
                Upload
              </button>
            ))}
          <button
            className="link danger"
            onClick={() => void deleteRow(row)}
            title={row.cloud ? 'Delete from repo' : 'Delete from this phone'}
            disabled={servedFromPhone && !!row.cloud}
          >
            ×
          </button>
        </div>
        {describeInfo(meta[row.name]) && <p className="muted small file-info">{describeInfo(meta[row.name])}</p>}
      </li>
    )
  }

  const errorBlock = errors.length > 0 && (
    <div className="errors">
      {errors.map((e, i) => (
        <p key={i} className="error">
          {e}
        </p>
      ))}
      <button className="link" onClick={() => setErrors([])}>
        Clear
      </button>
    </div>
  )

  // The Analysis tab's drawer: lap and diagram selection live in Analysis itself.
  const sidebar = <>{errorBlock}</>

  const repoPage = (
    <div className="tab-content">
      <div className="side-row">
        {nativePickerAvailable ? (
          <button className="button" onClick={() => void onPickNative()}>
            Open local session files…
          </button>
        ) : (
          <label className="button">
            Open local session files…
            <input type="file" accept=".xrk,.xrz,.hrz" multiple onChange={onPickFiles} hidden />
          </label>
        )}
        {!sampleOpen && sessions.length === 0 && cloud.length === 0 && docs.length === 0 && (
          <button className="link sample-link" onClick={() => void openSample()} disabled={busy > 0}>
            or try sample sessions
          </button>
        )}
      </div>
      {auth && !signedIn && merged.length === 0 && (
        <p className="muted small">
          Sign in to keep your sessions in a repo that follows you to every device.{' '}
          <button className="link" onClick={() => auth.requestSignIn('signin')}>
            Sign up / Sign in
          </button>
        </p>
      )}
      {(signedIn || merged.length > 0) && (
        <>
          {offlineAvailable && sync && signedIn && (
            <p className="muted small offline-status">
              {sync.pending > 0 ? `${sync.pending} session${sync.pending === 1 ? '' : 's'} waiting to upload · ` : ''}
              {sync.state === 'syncing'
                ? 'Syncing…'
                : sync.state === 'offline'
                  ? 'Offline: showing the copy on this phone'
                  : sync.state === 'error'
                    ? `Sync problem: ${sync.error}`
                    : `${sync.fileCount} file${sync.fileCount === 1 ? '' : 's'}, ${(sync.bytes / 1048576).toFixed(1)} MB on this phone`}
              {sync.syncedAt && sync.state !== 'syncing'
                ? ` · synced ${new Date(sync.syncedAt).toLocaleTimeString()}`
                : ''}
            </p>
          )}
          <div className="side-row repo-actions">
            {offlineAvailable && uploadable > 0 && (
              <button className="button" onClick={() => void uploadAllDocs()} disabled={busy > 0}>
                Upload all ({uploadable})
              </button>
            )}
            {signedIn && (
              <button className="button secondary" onClick={() => void onRefreshCloud()} disabled={refreshing}>
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </button>
            )}
            <button className="button secondary" onClick={closeAll} disabled={sessions.length === 0}>
              Close all
            </button>
          </div>
          <ul className="cloud-list">
            {merged.length === 0 && (
              <li className="muted">{servedFromPhone ? 'No files on this phone yet.' : 'Nothing uploaded yet.'}</li>
            )}
            {merged.map(renderRow)}
          </ul>
        </>
      )}
      {errorBlock}
    </div>
  )

  const devicePage = (
    <div className="tab-content">
      <Solo2Panel
        onFile={onLoggerFile}
        onUploadFile={auth ? (offlineAvailable ? onLoggerUpload : (n, b) => uploadAndOpen(n, b)) : undefined}
        canUpload={signedIn}
        requestSignIn={auth ? () => auth.requestSignIn('upload') : undefined}
        known={new Set([...sessions.map((s) => s.name), ...cloud.map((c) => c.name), ...docs.map((d) => d.name)])}
      />
      {errorBlock}
    </div>
  )

  return (
    <div className="studio">
      {pendingFiles.length > 0 && (
        <div className="modal-backdrop" onClick={() => setPendingFiles([])}>
          <div className="modal pick-choice" onClick={(e) => e.stopPropagation()}>
            <button className="link modal-close" onClick={() => setPendingFiles([])} aria-label="Close">
              ×
            </button>
            <p className="modal-note">
              {pendingFiles.length === 1 ? pendingFiles[0].name : `${pendingFiles.length} files selected`}
            </p>
            <button className="button" onClick={() => void openFiles(pendingFiles)}>
              Open
            </button>
            <button
              className="button secondary"
              onClick={() => void openAndUploadFiles(pendingFiles)}
              disabled={busy > 0}
            >
              Open and upload to my repo
            </button>
            <button className="link" onClick={() => setPendingFiles([])}>
              Cancel
            </button>
          </div>
        </div>
      )}
      <header className="studio-bar">
        <Link to="/" className="brand" title="Mala Racing home">
          Race Studio
        </Link>
        <span className="spacer" />
        {auth && signedIn && (
          <>
            <span className="user" title={auth.userLabel}>
              {auth.userLabel}
            </span>
            <button onClick={auth.signOut} className="link">
              Sign out
            </button>
          </>
        )}
        {auth && !signedIn && (
          <button onClick={() => auth.requestSignIn('signin')} className="button small-button">
            Sign up / Sign in
          </button>
        )}
      </header>
      <div className={`tab-pane${tab === 'analysis' ? '' : ' hidden'}`}>
        <div className="studio-toolbar">
          <button
            className="menu-toggle"
            aria-label={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}
            aria-expanded={sidebarOpen}
            onClick={() => setSidebarOpen((v) => !v)}
          >
            ☰
          </button>
          <div className="toolbar-actions">
            <button
              className="button secondary small-button"
              disabled={sessions.length === 0 || exportMode != null}
              title="Save the diagrams, racing lines and summary as one image"
              onClick={() => setExportMode('png')}
            >
              {exportMode === 'png' ? 'Exporting…' : 'Export'}
            </button>
            <button
              className="button small-button"
              disabled={sessions.length === 0 || exportMode != null}
              title="Send the comparison to an AI app with a prompt"
              onClick={() => setExportMode('ai')}
            >
              Ask AI
            </button>
          </div>
          {notice && <div className="toast">{notice}</div>}
        </div>

        <Analysis
          sessions={sessions}
          onRemove={removeSession}
          sidebar={sidebar}
          sidebarOpen={sidebarOpen}
          onCloseSidebar={() => setSidebarOpen(false)}
          busy={busy > 0 || !restored}
          view={view}
          setView={setView}
          restoredIds={restoredIds.current}
          aliases={aliases}
          exportMode={exportMode}
          onCloseExport={() => setExportMode(null)}
          onNotice={setNotice}
          onOpenRepo={() => switchTab('repo')}
        />
      </div>
      {/* Every tab stays mounted: the device connection and the repo list survive switching. */}
      <div className={`tab-page${tab === 'repo' ? '' : ' hidden'}`}>
        <main className="tab-main">{repoPage}</main>
      </div>
      {solo2Available && (
        <div className={`tab-page${tab === 'device' ? '' : ' hidden'}`}>
          <main className="tab-main">{devicePage}</main>
        </div>
      )}
      <TabBar tab={tab} onChange={switchTab} device={solo2Available} />
    </div>
  )
}

/** Two sessions are from different tracks when the logger named them differently, or, lacking names, when their start/finish lines are far apart. */
function differentTrack(a: Session, b: Session): boolean {
  if (a.meta.track && b.meta.track) return a.meta.track.trim().toLowerCase() !== b.meta.track.trim().toLowerCase()
  const sa = a.meta.startFinish
  const sb = b.meta.startFinish
  return !!sa && !!sb && haversine(sa.lat, sa.lon, sb.lat, sb.lon) > 500
}
